import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
// Eager: rutas públicas críticas (primer render sin espera)
import Homepage from './pages/Homepage';
import SearchResults from './pages/SearchResults';
import ListingDetail from './pages/ListingDetail';
// Lazy (code-splitting): el resto se descarga solo cuando se visita,
// reduciendo el bundle inicial para clientes.
const Checkout = lazy(() => import('./pages/Checkout'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const ClientProfile = lazy(() => import('./pages/ClientProfile'));
const ClientFavorites = lazy(() => import('./pages/ClientFavorites'));
const SubmitReview = lazy(() => import('./pages/SubmitReview'));
const ProviderDashboard = lazy(() => import('./pages/ProviderDashboard'));
const ProviderKYC = lazy(() => import('./pages/ProviderKYC'));
const ProviderAssets = lazy(() => import('./pages/ProviderAssets'));
const ProviderAvailability = lazy(() => import('./pages/ProviderAvailability'));
const PostAsset = lazy(() => import('./pages/PostAsset'));
const ProviderBookings = lazy(() => import('./pages/ProviderBookings'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminBookings = lazy(() => import('./pages/AdminBookings'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const ProviderVerification = lazy(() => import('./pages/ProviderVerification'));
const AdminClientVerification = lazy(() => import('./pages/AdminClientVerification'));
const AdminServiceReview = lazy(() => import('./pages/AdminServiceReview'));
const DisputeManagement = lazy(() => import('./pages/DisputeManagement'));
const CommissionConfig = lazy(() => import('./pages/CommissionConfig'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const AdminCategories = lazy(() => import('./pages/AdminCategories'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Terminos = lazy(() => import('./pages/Terminos'));
const Privacidad = lazy(() => import('./pages/Privacidad'));
const Cancelacion = lazy(() => import('./pages/Cancelacion'));
const SobreNosotros = lazy(() => import('./pages/SobreNosotros'));
const Contacto = lazy(() => import('./pages/Contacto'));
const Blog = lazy(() => import('./pages/Blog'));
const ProvidersLanding = lazy(() => import('./pages/ProvidersLanding'));
const Seguridad = lazy(() => import('./pages/Seguridad'));
const Entrega = lazy(() => import('./pages/Entrega'));
import { AuthProvider, useAuth } from './context/AuthContext';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import './i18n';
import './App.css';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  // Admins pueden acceder a cualquier sección protegida (cliente / proveedor / admin)
  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') return <Navigate to="/" />;
  return children;
};

function AppRouter() {
  const location = useLocation();

  // BUG-001 (ISO 25010 Fase 2): al navegar a otra ruta, posicionar la página al inicio
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Suspense fallback={<LoadingScreen />}>
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Públicas */}
        <Route path="/" element={<Homepage />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/buscar" element={<Navigate to="/search" replace />} />
        <Route path="/catalogo" element={<Navigate to="/search" replace />} />
        <Route path="/listing/:assetId" element={<ListingDetail />} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/booking-confirmation" element={<BookingConfirmation />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Legales + corporativas */}
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/cancelacion" element={<Cancelacion />} />
        <Route path="/sobre-nosotros" element={<SobreNosotros />} />
        <Route path="/contacto" element={<Contacto />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/proveedores" element={<ProvidersLanding />} />
        <Route path="/providers" element={<Navigate to="/proveedores" replace />} />
        <Route path="/seguridad" element={<Seguridad />} />
        <Route path="/entrega" element={<Entrega />} />

        {/* Cliente */}
        <Route path="/dashboard" element={<Navigate to="/client/dashboard" replace />} />
        <Route path="/client/dashboard" element={<ProtectedRoute requiredRole="client"><ClientDashboard /></ProtectedRoute>} />
        <Route path="/client/profile" element={<ProtectedRoute requiredRole="client"><ClientProfile /></ProtectedRoute>} />
        <Route path="/client/favorites" element={<ProtectedRoute requiredRole="client"><ClientFavorites /></ProtectedRoute>} />
        <Route path="/client/review/:bookingId" element={<ProtectedRoute requiredRole="client"><SubmitReview /></ProtectedRoute>} />

        {/* Proveedor */}
        <Route path="/provider/dashboard" element={<ProtectedRoute requiredRole="provider"><ProviderDashboard /></ProtectedRoute>} />
        <Route path="/provider/verificacion" element={<ProtectedRoute requiredRole="provider"><ProviderKYC /></ProtectedRoute>} />
        <Route path="/provider/assets" element={<ProtectedRoute requiredRole="provider"><ProviderAssets /></ProtectedRoute>} />
        <Route path="/provider/assets/:assetId/availability" element={<ProtectedRoute requiredRole="provider"><ProviderAvailability /></ProtectedRoute>} />
        <Route path="/provider/post-asset" element={<ProtectedRoute requiredRole="provider"><PostAsset /></ProtectedRoute>} />
        <Route path="/provider/listings/new" element={<Navigate to="/provider/post-asset" />} />
        <Route path="/provider/bookings" element={<ProtectedRoute requiredRole="provider"><ProviderBookings /></ProtectedRoute>} />

        {/* Admin */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
        <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/verification" element={<ProtectedRoute requiredRole="admin"><ProviderVerification /></ProtectedRoute>} />
        <Route path="/admin/clients/kyc" element={<ProtectedRoute requiredRole="admin"><AdminClientVerification /></ProtectedRoute>} />
        <Route path="/admin/servicios" element={<ProtectedRoute requiredRole="admin"><AdminServiceReview /></ProtectedRoute>} />
        <Route path="/admin/disputes" element={<ProtectedRoute requiredRole="admin"><DisputeManagement /></ProtectedRoute>} />
        <Route path="/admin/commission" element={<ProtectedRoute requiredRole="admin"><CommissionConfig /></ProtectedRoute>} />
        <Route path="/admin/commissions" element={<Navigate to="/admin/commission" />} />
        <Route path="/admin/reviews" element={<ProtectedRoute requiredRole="admin"><AdminReviews /></ProtectedRoute>} />
        <Route path="/admin/categories" element={<ProtectedRoute requiredRole="admin"><AdminCategories /></ProtectedRoute>} />
        <Route path="/admin/bookings" element={<ProtectedRoute requiredRole="admin"><AdminBookings /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/assets" element={<Navigate to="/admin/dashboard" replace />} />

        {/* Compatibilidad URL antigua — redirige /redmobility/* a / */}
        <Route path="/redmobility" element={<Navigate to="/" replace />} />
        <Route path="/redmobility/*" element={<Navigate to="/" replace />} />

        {/* 404 fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter basename="/">

      <ErrorBoundary>
        <AuthProvider>
          <AppRouter />
          <Footer />
          <BottomNav />
          <CookieBanner />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
